import { Assert } from '@cotera/utilities';
import _ from 'lodash';
import { err, ok, Result } from 'neverthrow';
import { Graph } from '../graph';
import { Commit, GraphCompiler } from './commit';
import { IdentityMap } from './identity-map';
import { renameSym, findAndUpdateLlmV2Columns } from './rename';
import { duplicateSym } from './duplicate';
import {
  OperationData,
  Operations,
  RenameFailure,
  UpdateExpr,
  UpdateDocument,
  UpdateAgent,
  UpdateOrReplaceColumn,
  UpdateRel,
} from './types';
import { EventStreamDefinition } from '../../manifest';
import { buildCommits } from './build-commits';
import { UpdateTool } from './schema';
import { Expression, Ty } from '@cotera/era';
import { GraphErrorResult } from '../graph/types';
import { DocumentEditor } from '../document';
import type { DeserializedBuildExplorerData } from './serialization';

export type HistItem = {
  readonly at: Graph.Identity;
  readonly identity: Graph.Identity;
  readonly op: OperationData;
  readonly commit: Commit;
};

export type SymbolInfo = {
  readonly builtNode: Graph.GraphResult;
  readonly rawNode: Graph.GraphNode;
};

export class BuildExplorer {
  readonly symbols: string[];
  readonly graph: Record<string, Graph.GraphResult>;
  readonly nodeMap: Record<string, Graph.GraphNode>;
  readonly parser: Graph.RelativeParser;
  readonly quickFix: Graph.QuickFixList;
  readonly compiler: GraphCompiler;

  static empty() {
    // Uses noToolsResolver since empty() is only for dependency extraction,
    // not for actual tool resolution
    return new this(
      {},
      IdentityMap.new(),
      [],
      undefined,
      Graph.noToolsResolver,
      undefined
    );
  }

  /**
   * Create a BuildExplorer from a graph of resolved nodes.
   * This is useful when you have a graph but need a BuildExplorer for dependency checking.
   */
  static fromGraph(
    graph: Record<string, Graph.Resolution>,
    toolResolver: Graph.IToolResolver = Graph.noToolsResolver
  ): BuildExplorer {
    const symbolMap: Record<string, Graph.Identity> = {};
    const identityMapEntries: [Graph.Identity, Graph.GraphNode][] = [];

    // Extract raw nodes and identities from the graph
    for (const [symbol, resolution] of Object.entries(graph)) {
      const { item } = resolution;

      // Extract the raw node and identity based on item type
      let rawNode: Graph.GraphNode;
      let identity: Graph.Identity;
      switch (item.t) {
        case 'rel':
          rawNode = item.relNode;
          identity = item.identity;
          break;
        case 'expr':
          rawNode = item.exprNode;
          identity = rawNode.meta.identity;
          break;
        case 'document':
          rawNode = item.documentNode;
          identity = rawNode.meta.identity;
          break;
        case 'agent':
          rawNode = item.agentNode;
          identity = rawNode.meta.identity;
          break;
        case 'tool':
          // Tool items don't have a raw node in the same way
          // Skip tools for now as they're not needed for dependency checking
          continue;
        default:
          return Assert.unreachable(item);
      }

      symbolMap[symbol] = identity;
      identityMapEntries.push([identity, rawNode]);
    }

    // Create IdentityMap from entries
    let identityMap = IdentityMap.new();
    for (const [identity, node] of identityMapEntries) {
      identityMap = identityMap.add(identity, node);
    }

    return new this(
      symbolMap,
      identityMap,
      [],
      undefined,
      toolResolver,
      undefined
    );
  }

  /**
   * Create a BuildExplorer from deserialized data received from a worker thread.
   * This allows graph compilation to happen off the main thread.
   */
  static fromDeserializedData(
    data: DeserializedBuildExplorerData,
    toolResolver: Graph.IToolResolver
  ): BuildExplorer {
    const identities = IdentityMap.fromMap(data.identitiesMap);
    return new this(
      data.symbolMap,
      identities,
      [...data.commits],
      undefined,
      toolResolver,
      data.graph // Pass pre-resolved graph to skip Graph.resolve()
    );
  }

  forRel(rel: string): BuildExplorer {
    return new BuildExplorer(
      this.symbolMap,
      this.identities,
      this.commits,
      this.parser.forRel(rel),
      this.toolResolver,
      undefined // Don't pass pre-resolved graph, let it re-resolve for the new rel context
    );
  }

  constructor(
    readonly symbolMap: Record<string, Graph.Identity>,
    readonly identities: IdentityMap,
    readonly commits: readonly Commit[],
    parser: Graph.RelativeParser | undefined,
    readonly toolResolver: Graph.IToolResolver,
    preResolvedGraph?: Record<string, Graph.GraphResult>
  ) {
    const nodes = _.mapValues(symbolMap, (ident) =>
      Assert.assertExists(identities.get(ident))
    );

    // Use pre-resolved graph if provided (from worker thread), otherwise resolve
    this.graph = preResolvedGraph ?? Graph.resolve(nodes, { toolResolver });
    this.parser = parser ?? new Graph.GraphRelativeParser(this.graph);
    this.quickFix = Graph.quickFix(this.graph);
    this.nodeMap = nodes;
    this.symbols = Object.keys(this.symbolMap).sort();
    this.compiler = GraphCompiler.fromCommits([...commits], toolResolver);

    for (const val of Object.values(this)) {
      Object.freeze(val);
    }

    Object.freeze(this);
  }

  builtNodes(): Record<string, Graph.Resolution> {
    return Object.fromEntries(
      Object.entries(this.graph)
        .map(([name, result]) =>
          result.isOk() ? ([name, result.value] as const) : null
        )
        .filter((val) => val !== null)
    );
  }

  tools(): Record<string, Graph.ToolItem> {
    const tools = Object.entries(this.graph)
      .map(([name, result]) =>
        result.isOk() && result.value.item.t === 'tool'
          ? ([name, result.value.item] as const)
          : null
      )
      .filter((val) => val !== null);

    return Object.fromEntries(tools);
  }

  eventStreams(): EventStreamDefinition[] {
    return Object.values(this.graph)
      .flatMap((node) =>
        node
          .map((x) => (x.item.t === 'rel' ? Object.values(x.item.cols) : []))
          .unwrapOr([])
      )
      .map((col) => col.eventStream)
      .filter((stream) => stream !== null);
  }

  allItemsAreValid(): boolean {
    return Object.values(this.graph).every((x) => x.isOk());
  }

  itemErrors(): Record<string, Graph.GraphError> {
    const errs: Record<string, Graph.GraphError> = {};
    for (const [name, res] of Object.entries(this.graph)) {
      if (res.isErr()) {
        errs[name] = res.error.err;
      }
    }
    return errs;
  }

  forSymbol(sym: string): IGlobalSymbolExplorer | null {
    const ident = this.symbolMap[sym];

    if (ident === undefined) {
      return null;
    }

    const node = this.graph[sym];

    if (node === undefined) {
      return null;
    }
    const rawNode = this.identities.get(ident);

    if (rawNode === null) {
      return null;
    }

    if (rawNode.t === 'rel') {
      return new RelBuildExplorer(sym, this, { builtNode: node, rawNode });
    } else if (rawNode.t === 'expr') {
      return new ExprBuildExplorer(sym, this, { builtNode: node, rawNode });
    } else if (rawNode.t === 'document') {
      return new DocumentBuildExplorer(sym, this, { builtNode: node, rawNode });
    } else if (rawNode.t === 'agent') {
      return new AgentBuildExplorer(sym, this, { builtNode: node, rawNode });
    } else if (rawNode.t === 'tool') {
      return new ToolBuildExplorer(sym, this, { builtNode: node, rawNode });
    } else {
      return new ExprBuildExplorer(sym, this, { builtNode: node, rawNode });
    }
  }

  hasSymbol(sym: string): boolean {
    return Object.hasOwn(this.symbolMap, sym);
  }

  historyForColumn(col: { rel: string; name: string }): HistItem[] | null {
    const column = this.getColumn(col);
    if (column === null) {
      return null;
    }

    return this.hydrateOps(column.meta.editsAt);
  }

  hasCol(col: { rel: string; name: string }): boolean {
    return this.getColumn(col) !== null;
  }

  getRel(rel: { symbol: string }): RelBuildExplorer | null {
    const node = this.forSymbol(rel.symbol);

    if (node === null) {
      return null;
    }

    if (node.rawNode.t !== 'rel') {
      return null;
    }

    return node as RelBuildExplorer;
  }

  getExpr(expr: { symbol: string }): ExprBuildExplorer | null {
    const node = this.forSymbol(expr.symbol);

    if (node === null) {
      return null;
    }

    if (node.rawNode.t !== 'expr') {
      return null;
    }

    return node as ExprBuildExplorer;
  }

  getDocument(doc: { symbol: string }): DocumentBuildExplorer | null {
    const node = this.forSymbol(doc.symbol);

    if (node === null) {
      return null;
    }

    if (node.rawNode.t !== 'document') {
      return null;
    }

    return node as DocumentBuildExplorer;
  }

  getAgent(agent: { symbol: string }): AgentBuildExplorer | null {
    const node = this.forSymbol(agent.symbol);

    if (node === null) {
      return null;
    }

    if (node.rawNode.t !== 'agent') {
      return null;
    }

    return node as AgentBuildExplorer;
  }

  getAgentByIdentity(params: {
    agentIdentity: Graph.Identity;
  }): AgentBuildExplorer | null {
    const agentEntry = Object.entries(this.symbolMap).find(([_sym, ident]) =>
      ident.eq(params.agentIdentity)
    );
    if (agentEntry === undefined) {
      return null;
    }

    const [agentSym, _] = agentEntry;
    const agentInfo = this.forSymbol(agentSym);

    if (agentInfo === null) {
      return null;
    }

    return agentInfo as AgentBuildExplorer;
  }

  at(at: Graph.Identity): BuildExplorer {
    return Assert.assertOk(
      buildCommits(
        this.commits
          .filter((_, index) => index <= at.commitIndex)
          .map((c, index) => {
            // Only filter operations for the target commit
            // Earlier commits should retain all their operations
            if (index === at.commitIndex) {
              return {
                ...c,
                operations: c.operations.filter(
                  (_, opIndex) => opIndex <= at.opIndex
                ),
              };
            } else {
              // Keep all operations for earlier commits
              return c;
            }
          }),
        { toolResolver: this.toolResolver }
      )
    );
  }

  getBuiltRelByIdentity(params: { relIdentity: Graph.Identity }):
    | (Graph.RelNode & {
        readonly symbol: string;
      })
    | null {
    const relEntry = Object.entries(this.symbolMap).find(([_sym, ident]) =>
      ident.eq(params.relIdentity)
    );

    if (relEntry === undefined) {
      return null;
    }

    const [relSym, _] = relEntry;

    const rel = this.identities.get(params.relIdentity);

    if (rel === null) {
      return null;
    }

    if (rel.t !== 'rel') {
      return null;
    }

    return {
      ...rel,
      symbol: relSym,
    };
  }

  getExprByIdentity(params: {
    exprIdentity: Graph.Identity;
  }): GlobalSymbolExplorer<UpdateExpr> | null {
    const exprEntry = Object.entries(this.symbolMap).find(([_sym, ident]) =>
      ident.eq(params.exprIdentity)
    );
    if (exprEntry === undefined) {
      return null;
    }

    const [exprSym, _] = exprEntry;
    const exprInfo = this.forSymbol(exprSym);

    if (exprInfo === null) {
      return null;
    }

    if (exprInfo.builtNode.isErr()) {
      return null;
    }

    if (exprInfo.builtNode.value.item.t !== 'expr') {
      return null;
    }

    return exprInfo as ExprBuildExplorer;
  }

  getBuiltColumnByIdentity(params: {
    relIdentity: Graph.Identity;
    colIdentity: Graph.Identity;
  }):
    | (Graph.BuiltColNode & {
        readonly symbol: string;
      })
    | null {
    const relEntry = Object.entries(this.symbolMap).find(([_sym, ident]) =>
      ident.eq(params.relIdentity)
    );

    if (relEntry === undefined) {
      return null;
    }

    const [relSym, _] = relEntry;
    const relInfo = this.forSymbol(relSym);

    if (relInfo === null) {
      return null;
    }

    if (relInfo.builtNode.isErr()) {
      return null;
    }

    if (relInfo.builtNode.value.item.t !== 'rel') {
      return null;
    }

    const colInfo = Object.entries(relInfo.builtNode.value.item.cols).find(
      ([_colName, node]) => node.identity.eq(params.colIdentity)
    );
    return colInfo?.[1]
      ? {
          ...colInfo[1],
          symbol: colInfo[0],
        }
      : null;
  }

  getColumn(col: {
    readonly rel: string;
    readonly name: string;
  }): ColumnBuildExplorer | null {
    const symbolInfo = this.forSymbol(col.rel);

    if (symbolInfo === null) {
      return null;
    }

    if (symbolInfo.rawNode.t === 'rel') {
      if (symbolInfo.rawNode.cols[col.name] === undefined) {
        return null;
      }

      return new ColumnBuildExplorer(
        { relName: col.rel, colName: col.name },
        symbolInfo.rawNode.cols[col.name]!,
        this
      );
    }

    return null;
  }

  historyForSymbol(sym: string): HistItem[] | null {
    const base = this.forSymbol(sym);

    if (base === null) {
      return null;
    }

    return this.hydrateOps(base.rawNode.meta.editsAt);
  }

  private hydrateOps(ats: readonly Graph.Identity[]): HistItem[] {
    // For each identity, find the corresponding operation in the commits
    const ops = ats.map((at) => {
      const [commitIndex, opIndex] = at.asTuple();

      // Find the commit at the specified index
      const commit = this.commits[commitIndex];
      if (!commit) {
        throw new Error(`Commit at index ${commitIndex} not found`);
      }

      // Find the operation at the specified index within that commit
      const op = commit.operations[opIndex];
      if (!op) {
        throw new Error(
          `Operation at index ${opIndex} not found in commit ${commitIndex}`
        );
      }

      return { at, op, commit };
    });

    if (ops.length === 0) {
      return [];
    }

    let currentIdent = ops[0]!.at;
    const result: HistItem[] = [];
    for (const { at, op, commit } of ops.sort((a, b) => a.at.compareTo(b.at))) {
      if (op.t === 'create-column') {
        currentIdent = at;
      } else if (op.t === 'replace-column') {
        currentIdent = at;
      }
      result.push({ at, op, commit, identity: currentIdent });
    }

    return result;
  }
}

type IGlobalSymbolExplorer<UpdateOp extends OperationData = OperationData> = {
  symbol: string;

  builtNode: Graph.GraphResult;

  rawNode: Graph.GraphNode;

  makeRenameOps(params: { to: string }): Result<OperationData[], RenameFailure>;

  makeDuplicateOps(params: {
    to: string;
  }): Result<OperationData[], RenameFailure>;

  makeUpdateOps(
    params: Omit<UpdateOp, 't' | 'message'>
  ): Result<OperationData[], never>;

  makeDeleteOps(): Result<OperationData[], never>;
};

abstract class GlobalSymbolExplorer<
  UpdateOp extends OperationData = OperationData
> implements IGlobalSymbolExplorer<UpdateOp>
{
  constructor(
    readonly symbol: string,
    private readonly buildExplorer: BuildExplorer,
    private readonly symbolInfo: SymbolInfo
  ) {
    Object.freeze(this);
  }

  get rawNode() {
    return this.symbolInfo.rawNode;
  }

  get builtNode() {
    return this.symbolInfo.builtNode;
  }

  makeRenameOps(params: {
    to: string;
  }): Result<OperationData[], RenameFailure> {
    const { to } = params;

    if (this.buildExplorer.hasSymbol(to)) {
      return err({ t: 'symbol-taken' });
    }

    const errors: Record<string, Graph.GraphError> = {};
    const graph: Record<string, Graph.Resolution> = {};

    for (const [name, res] of Object.entries(this.buildExplorer.graph)) {
      if (res.isOk()) {
        graph[name] = res.value;
      } else {
        errors[name] = res.error.err;
      }
    }

    if (Object.keys(errors).length > 0) {
      return err({ t: 'has-errors', errors });
    } else {
      return ok(renameSym({ from: this.symbol, to, graph }));
    }
  }

  makeDuplicateOps(params: {
    to: string;
  }): Result<OperationData[], RenameFailure> {
    const { to } = params;

    if (this.buildExplorer.hasSymbol(to)) {
      return err({ t: 'symbol-taken' });
    }

    const errors: Record<string, Graph.GraphError> = {};
    const graph: Record<string, Graph.Resolution> = {};

    for (const [name, res] of Object.entries(this.buildExplorer.graph)) {
      if (res.isOk()) {
        graph[name] = res.value;
      } else {
        errors[name] = res.error.err;
      }
    }

    if (Object.keys(errors).length > 0) {
      return err({ t: 'has-errors', errors });
    } else {
      return ok(duplicateSym({ from: this.symbol, to, graph }));
    }
  }

  makeDeleteOps(): Result<OperationData[], never> {
    return ok([Operations.delete({ symbol: this.symbol })]);
  }

  abstract makeUpdateOps(
    params: Omit<UpdateOp, 't' | 'message'>
  ): Result<OperationData[], never>;
}

class RelBuildExplorer extends GlobalSymbolExplorer<UpdateRel> {
  makeUpdateOps(
    params: Omit<UpdateRel, 't' | 'message'>
  ): Result<OperationData[], never> {
    return ok([Operations.updateRel(params)]);
  }
}

class ExprBuildExplorer extends GlobalSymbolExplorer<UpdateExpr> {
  makeUpdateOps(
    params: Omit<UpdateExpr, 't' | 'message'>
  ): Result<OperationData[], never> {
    return ok([Operations.updateExpr(params)]);
  }
}

class DocumentBuildExplorer extends GlobalSymbolExplorer<UpdateDocument> {
  makeUpdateOps(
    params: Omit<UpdateDocument, 't' | 'message'>
  ): Result<OperationData[], never> {
    return ok([Operations.updateDocument(params)]);
  }
}

class AgentBuildExplorer extends GlobalSymbolExplorer<UpdateAgent> {
  makeUpdateOps(
    params: Omit<UpdateAgent, 't' | 'message'>
  ): Result<OperationData[], never> {
    return ok([Operations.updateAgent(params)]);
  }

  getPrompt(): Result<
    {
      tools: string[];
      outputTy: Ty.ExtendedAttributeType;
      prompt: Expression;
      model: string;
      temperature: number;
      maxOutputTokens: number;
    },
    GraphErrorResult
  > {
    if (this.builtNode.isErr()) {
      return err(this.builtNode.error);
    }

    Assert.assert(this.builtNode.value.item.t === 'agent');

    const agentConfig = this.builtNode.value.item;

    new DocumentEditor.EditorState(agentConfig.editorState);

    throw new Error('Not implemented');
  }
}

class ToolBuildExplorer extends GlobalSymbolExplorer<UpdateTool> {
  makeUpdateOps(
    params: Omit<UpdateTool, 't' | 'message'>
  ): Result<OperationData[], never> {
    return ok([Operations.updateTool(params)]);
  }
}

export class ColumnBuildExplorer {
  constructor(
    private readonly symbol: {
      relName: string;
      colName: string;
    },
    private readonly col: Graph.ColNode,
    private readonly buildExplorer: BuildExplorer
  ) {}

  get ty() {
    const rel = this.buildExplorer.getRel({ symbol: this.symbol.relName });
    if (rel === null) {
      return null;
    }

    const builtRel = rel.builtNode;
    if (builtRel.isErr()) {
      return null;
    }

    if (builtRel.value.item.t !== 'rel') {
      return null;
    }

    return builtRel.value.item.rel.attributes[this.symbol.colName] ?? null;
  }

  get rawNode() {
    return this.col;
  }

  get meta() {
    return this.col.meta;
  }

  get config(): Graph.ColConfig {
    return this.col.def;
  }

  get builtNode(): Graph.BuiltColNode | null {
    const symbolInfo = this.buildExplorer.forSymbol(this.symbol.relName);

    if (symbolInfo === null) return null;

    return symbolInfo.builtNode.isOk() &&
      symbolInfo.builtNode.value.item.t === 'rel'
      ? symbolInfo.builtNode.value.item.cols[this.symbol.colName] ?? null
      : null;
  }

  history(): (HistItem & { col: Graph.BuiltColNode })[] {
    const history = this.buildExplorer.historyForColumn({
      rel: this.symbol.relName,
      name: this.symbol.colName,
    });
    if (history === null) {
      return [];
    }

    return history.map((h) => {
      const exploreForIdent = this.buildExplorer.at(h.identity);

      Assert.assert(exploreForIdent !== null);

      const op = h.op;

      //This is annoying, but we need the relName
      //so we assert it must be an operation that has a relName
      Assert.assert(
        op.t === 'create-column' ||
          op.t === 'replace-column' ||
          op.t === 'update-column' ||
          op.t === 'delete-col' ||
          op.t === 'rename-col'
      );

      const rel = exploreForIdent.getRel({ symbol: op.relName });

      Assert.assert(rel !== null);

      const relIdentity = rel.rawNode.meta.identity;

      const col = exploreForIdent.getBuiltColumnByIdentity({
        relIdentity,
        colIdentity: h.identity,
      });

      Assert.assert(col !== null);

      return {
        ...h,
        col,
      };
    });
  }

  makeRenameOps(params: {
    to: string;
  }): Result<OperationData[], RenameFailure> {
    const { to } = params;

    if (this.buildExplorer.hasCol({ rel: this.symbol.relName, name: to })) {
      return err({ t: 'symbol-taken' });
    }

    const ops: OperationData[] = [
      Operations.renameCol({
        relName: this.symbol.relName,
        name: this.symbol.colName,
        newName: to,
      }),
    ];

    // Find and update LLM v2 columns that reference this column
    // Check all relations in the graph
    for (const symbol of this.buildExplorer.symbols) {
      const symbolInfo = this.buildExplorer.forSymbol(symbol);
      if (symbolInfo === null || symbolInfo.rawNode.t !== 'rel') {
        continue;
      }

      const relNode = symbolInfo.rawNode;
      const llmColUpdates = findAndUpdateLlmV2Columns({
        relSymbol: symbol,
        relNode,
        from: this.symbol.colName,
        to,
        isColumnRename: true,
        graphExplorer: this.buildExplorer,
      });
      ops.push(...llmColUpdates);
    }

    return ok(ops);
  }

  makeDuplicateOps(params: {
    to: string;
  }): Result<OperationData[], RenameFailure> {
    const { to } = params;

    if (this.buildExplorer.hasCol({ rel: this.symbol.relName, name: to })) {
      return err({ t: 'symbol-taken' });
    }

    return ok([
      Operations.duplicate({
        relName: this.symbol.relName,
        name: this.symbol.colName,
        newName: to,
        newSymbol: this.symbol.relName, // Target relation (same as source for same-relation duplication)
      }),
    ]);
  }

  makeReplaceOps(
    params: Pick<UpdateOrReplaceColumn, 'config'>
  ): Result<OperationData[], never> {
    return ok([
      Operations.replaceColumn({
        relName: this.symbol.relName,
        name: this.symbol.colName,
        config: { ...this.config, ...params.config },
      }),
    ]);
  }

  makeDeleteOps(): Result<OperationData[], never> {
    return ok([
      Operations.deleteCol({
        relName: this.symbol.relName,
        name: this.symbol.colName,
      }),
    ]);
  }

  makeUpdateOps(
    params: Omit<
      UpdateOrReplaceColumn,
      't' | 'message' | 'k' | 'relName' | 'name'
    >
  ): Result<OperationData[], never> {
    return ok([
      Operations.updateColumn({
        ...params,
        relName: this.symbol.relName,
        name: this.symbol.colName,
      }),
    ]);
  }
}
